import { useCallback, useEffect, useRef, useState } from 'react';

import { graphql, useMutation } from 'react-relay';

import type { InferredProjectHydrationFailureReason } from '../entity-annotations/types';
import type { useInferredProjectContextMutation } from './__generated__/useInferredProjectContextMutation.graphql';
import {
	type InferredProjectContext,
	parseInferredProjectContext,
} from './parseInferredProjectContext';

const PROJECT_CONTEXT_ACTION_ID = 'd1acdf80-fa49-460b-873e-d30a97275f52';

type JsonRecord = Record<string, unknown>;

export type InferredProjectContextState =
	| { data?: undefined; status: 'idle' | 'loading' }
	| { data: InferredProjectContext; status: 'success' }
	| { data?: undefined; status: 'error' };

const INITIAL_STATE: InferredProjectContextState = { status: 'idle' };

const isRecord = (value: unknown): value is JsonRecord =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const isFailureResponse = (outputs: unknown): boolean =>
	isRecord(outputs) &&
	(outputs.operationStatus === 'FAILURE' ||
		(typeof outputs.status === 'number' && outputs.status >= 400));

export const useInferredProjectContext = ({
	cloudId,
	projectId,
	onHydrationFailure,
}: {
	cloudId: string;
	onHydrationFailure?: (reason: InferredProjectHydrationFailureReason, projectId: string) => void;
	projectId: string;
}): InferredProjectContextState & { load: () => void } => {
	const [state, setState] = useState<InferredProjectContextState>(INITIAL_STATE);
	const inFlightRef = useRef(false);
	const hasSucceededRef = useRef(false);
	const disposeRef = useRef<(() => void) | null>(null);

	const [commit] = useMutation<useInferredProjectContextMutation>(graphql`
		mutation useInferredProjectContextMutation(
			$actionInput: ActionsExecuteActionInput!
			$filter: ActionsExecuteActionFilter!
		) {
			actions {
				execute(actionInput: $actionInput, filter: $filter) {
					outputs
					status
				}
			}
		}
	`);

	useEffect(() => {
		disposeRef.current?.();
		disposeRef.current = null;
		inFlightRef.current = false;
		hasSucceededRef.current = false;
		setState(INITIAL_STATE);

		return () => {
			disposeRef.current?.();
			disposeRef.current = null;
		};
	}, [cloudId, projectId]);

	const load = useCallback(() => {
		if (!cloudId || !projectId || inFlightRef.current || hasSucceededRef.current) {
			return;
		}

		inFlightRef.current = true;
		setState({ status: 'loading' });

		// Capture the callback for this attempt so a later render cannot reattribute its failure.
		const reportFailure = (reason: InferredProjectHydrationFailureReason) => {
			setState({ status: 'error' });
			onHydrationFailure?.(reason, projectId);
		};

		const disposable = commit({
			variables: {
				actionInput: {
					cloudId,
					inputs: JSON.stringify({
						projectId,
						include: ['details'],
						includeInferredProjectMetadata: true,
					}),
				},
				filter: { actionId: PROJECT_CONTEXT_ACTION_ID },
			},
			onCompleted: (response, errors) => {
				inFlightRef.current = false;
				disposeRef.current = null;
				if (errors?.length) {
					reportFailure('graphql_error');
					return;
				}
				const executeResponse = response.actions?.execute;
				if (
					!executeResponse ||
					(typeof executeResponse.status === 'number' && executeResponse.status >= 400) ||
					isFailureResponse(executeResponse.outputs)
				) {
					reportFailure(executeResponse ? 'api_failure' : 'missing_response');
					return;
				}

				hasSucceededRef.current = true;
				setState({
					status: 'success',
					data: parseInferredProjectContext(executeResponse.outputs),
				});
			},
			onError: () => {
				inFlightRef.current = false;
				disposeRef.current = null;
				reportFailure('transport_error');
			},
		});

		disposeRef.current = () => disposable.dispose();
	}, [cloudId, commit, onHydrationFailure, projectId]);

	return { ...state, load };
};
